import { useCallback, useRef, useState } from 'react'
import {
  ProxyRequestRow,
  ProxyRequestSkelton,
  fetchProxyRequest,
  ProxyRequestBatchInput,
  ProxyRequestInput,
  convertInput2Date,
  convertBreakTime,
  updateBatch,
  ProxyRequestRowScheduleBody,
  ProxyRequestRowActualBody,
  calculateWorkTime,
  ProxyRequestRowProjectBody,
  fetchTargetUsers,
  StandardWorkingInfo,
  ProxyRequestSummaryRow,
  ProxyRequestSummaryRowProjectBody,
  calculateBreakTime,
  RequestStatusRowProjectBody,
  RequestStatus,
  ProxyRequestRequestBatchInput,
  ProxyRequestRequestInput,
  updateRequestBatch,
  ProxyRequestRatioRow,
  RequestRatioRowProjectBody,
} from '../ProxyRequest'
import { AgGridTreeHelper } from '../../../containers/BulkSheetView/lib/tree'
import _ from 'lodash'
import store from '../../../../store'
import {
  doNotRequireSave,
  requireSave,
} from '../../../../store/requiredSaveData'
import { APIResponse } from '../../../../lib/commons/api'
import { generateUuid } from '../../../../utils/uuids'
import Auth from '../../../../lib/commons/auth'
import DateVO from '../../../../vo/DateVO'
import TaskActualWorkAPI, {
  TaskActualWorkHour,
} from '../../../../lib/functions/taskActualWork'
import { UserBasic } from '../../../../domain/value-object/UserBasicVO'
import { featureFlagService } from '../../../../utils/feature-flag'
import { calculateRatio } from '../gridOptions'
import { convertDecimalHourToSeconds } from '../../../../utils/time'

export const useProxyRequestData = (projectUuid: string) => {
  const latestUserUuidRef = useRef<string | undefined>()
  const [summaryRow, setSummaryRow] = useState<ProxyRequestSummaryRow>(
    new ProxyRequestSummaryRow()
  )
  const [ratioRow, setRatioRow] = useState<ProxyRequestRatioRow>(
    new ProxyRequestRatioRow()
  )
  const [data, setData] = useState<ProxyRequestRow[]>([])
  const originalData = useRef<
    Map<
      string,
      Pick<
        ProxyRequestRowActualBody,
        'workingHoursType' | 'startTime' | 'endTime' | 'breakTime' | 'remarks'
      >
    >
  >(new Map())

  const clearData = useCallback(() => {
    setData([])
    originalData.current.clear()
    setSummaryRow(new ProxyRequestSummaryRow())
    setRatioRow(new ProxyRequestRatioRow())
  }, [])

  const fetchRecords = useCallback(
    async (
      userUuid: string,
      projectUuid: string,
      startDate: string,
      endDate: string
    ) => {
      setData([])
      const response = await fetchProxyRequest({
        projectUuid,
        userUuid,
        startDate,
        endDate,
      })
      const sources: ProxyRequestSkelton[] = response.json

      const responseTaskActualHour =
        await TaskActualWorkAPI.getTaskActualWorkHours({
          projectUuid: undefined,
          userUuid,
          startDate,
          endDate,
        })
      const taskActualWork: TaskActualWorkHour[] = responseTaskActualHour.json
      const rows = sources
        .map(v =>
          AgGridTreeHelper.convert(
            v,
            (s: ProxyRequestSkelton): ProxyRequestRow => {
              const targetTaskActualWorks = taskActualWork.filter(
                actualWork => actualWork.actualDate === s.workDate
              )
              let totalHour = 0

              totalHour = targetTaskActualWorks.reduce(
                (acc, cur) => acc + convertDecimalHourToSeconds(cur.hour),
                0
              )
              const row: ProxyRequestRow = {
                type: 'WORK_HOUR',
                uuid: s.uuid,
                date: s.workDate,
                schedule: s.schedule
                  ? new ProxyRequestRowScheduleBody(s.schedule)
                  : undefined,
                actual: s.actual
                  ? new ProxyRequestRowActualBody(s.actual)
                  : undefined,
                totalActualHour: totalHour,
                projectHours: targetTaskActualWorks.map(actualWork => {
                  const body = new ProxyRequestRowProjectBody()
                  body.projectUuid = actualWork.projectUuid
                  body.actualHour = convertDecimalHourToSeconds(actualWork.hour)
                  return body
                }),
                requestCheck: requestStatusCheck(targetTaskActualWorks),
                projectRequestStatus: targetTaskActualWorks.map(
                  actualWorkStatus => {
                    const body = new RequestStatusRowProjectBody()
                    body.projectUuid = actualWorkStatus.projectUuid
                    body.requestStatus = actualWorkStatus.requestStatus
                    body.requestUuid = actualWorkStatus.requestUuid ?? undefined
                    body.proxyUser = actualWorkStatus.proxyUser
                    return body
                  }
                ),
              } as ProxyRequestRow

              targetTaskActualWorks.forEach(actualWork => {
                const fieldName = `project_${actualWork.projectUuid}_requestCheck`
                row[fieldName] =
                  actualWork.requestStatus === RequestStatus.REQUESTED ||
                  actualWork.requestStatus === RequestStatus.APPROVED
              })

              row.workingHoursType =
                row.actual?.workingHoursType ??
                row.schedule?.workingHoursType ??
                ''
              row.remarks = row.actual?.remarks ?? row.schedule?.dateName ?? ''
              return row
            }
          )
        )
        .flat()

      if (latestUserUuidRef.current !== userUuid) return

      setData(rows)
      originalData.current.clear()

      let sumBreak = 0
      let sumWorked = 0
      rows
        .filter(row => !!row.actual)
        .forEach(row => {
          originalData.current.set(
            row.uuid,
            _.cloneDeep({
              workingHoursType: row.actual!.workingHoursType,
              startTime: row.actual!.startTime,
              endTime: row.actual!.endTime,
              breakTime: row.actual!.breakTime,
              remarks: row.actual!.remarks,
            })
          )
          sumBreak += calculateBreakTime(row)
          sumWorked += calculateWorkTime(row)
        })

      const projectHourMap = new Map<
        string,
        ProxyRequestSummaryRowProjectBody
      >()
      rows.forEach(row => {
        row.projectHours?.forEach(projectHour => {
          const body = projectHourMap.has(projectHour.projectUuid)
            ? (projectHourMap.get(
                projectHour.projectUuid
              ) as ProxyRequestSummaryRowProjectBody)
            : new ProxyRequestSummaryRowProjectBody(projectHour.projectUuid)
          body.actualHour += projectHour.actualHour
          projectHourMap.set(projectHour.projectUuid, body)
        })
        return projectHourMap
      })

      const projectRequestMap = new Map<string, RequestRatioRowProjectBody>()

      rows.forEach(row => {
        row.projectRequestStatus?.forEach(projectStatus => {
          const body = projectRequestMap.has(projectStatus.projectUuid)
            ? (projectRequestMap.get(
                projectStatus.projectUuid
              ) as RequestRatioRowProjectBody)
            : new RequestRatioRowProjectBody(projectStatus.projectUuid)
          if (projectStatus.requestStatus !== undefined) {
            body.requestStatusConunt = (body.requestStatusConunt ?? 0) + 1
          }
          if (projectStatus.requestStatus === RequestStatus.APPROVED) {
            body.approvedStatusConunt = (body.approvedStatusConunt ?? 0) + 1
          }
          projectRequestMap.set(projectStatus.projectUuid, body)
        })
      })
      const sumRow = new ProxyRequestSummaryRow()
      sumRow.breakTime = sumBreak
      sumRow.workHour = sumWorked
      sumRow.totalActualHour = rows.reduce((sum, row) => {
        return sum + (row.totalActualHour || 0)
      }, 0)
      sumRow.projectHours = Array.from(projectHourMap.values())
      setSummaryRow(sumRow)
      if (featureFlagService.isEnabled('task-actual-work-application')) {
        const ratioRow = new ProxyRequestRatioRow()
        const totalHour = rows.reduce((sum, row) => {
          return sum + (row.totalActualHour || 0)
        }, 0)
        ratioRow.totalActualHour = totalHour
        ratioRow.totalActualHourRatio =
          sumWorked > 0 ? calculateRatio(totalHour, sumWorked / 3600) : 0
        ratioRow.projectHours = Array.from(projectHourMap.values())
        ratioRow.projectRequestStatus = Array.from(projectRequestMap.values())
        setRatioRow(ratioRow)
      }
    },
    []
  )

  const requestStatusCheck = useCallback(
    (row: TaskActualWorkHour[]): boolean => {
      if (row.length === 0) return false
      const statusList = row.map(
        actualWorkStatus => actualWorkStatus.requestStatus
      )
      return statusList.every(
        status =>
          status === RequestStatus.APPROVED ||
          status === RequestStatus.REQUESTED
      )
    },
    []
  )

  const refresh = useCallback(
    async (
      userUuid: string,
      projectUuid: string,
      startDate: string,
      endDate: string
    ) => {
      latestUserUuidRef.current = userUuid
      const formatStart = new DateVO(startDate).formatForApi()!
      const formatEnd = new DateVO(endDate).formatForApi()!
      await fetchRecords(userUuid, projectUuid, formatStart, formatEnd)
      store.dispatch(doNotRequireSave())
    },
    [fetchRecords]
  )

  const updateWorkingRequest = useCallback(
    async (
      rows: ProxyRequestRow[],
      targetUserUuid: string
    ): Promise<APIResponse> => {
      const tenant = Auth.getCurrentTenant()
      const proxyUserUuid = tenant!.user!.uuid
      const batchInput: ProxyRequestRequestBatchInput = {
        application: convertRow3Request(
          rows
            .map(row => ({
              ...row,
              projectRequestStatus: row.projectRequestStatus?.filter(
                status =>
                  status.requestUuid === undefined &&
                  status.requestStatus === undefined
              ),
            }))
            .filter(
              row =>
                row.projectRequestStatus && row.projectRequestStatus.length > 0
            ),
          targetUserUuid
        ),
        reapplication: convertRow3Request(
          rows
            .map(row => ({
              ...row,
              projectRequestStatus: row.projectRequestStatus?.filter(
                status =>
                  status.requestUuid !== undefined &&
                  status.requestStatus === RequestStatus.SENT_BACK
              ),
            }))
            .filter(
              row =>
                row.projectRequestStatus && row.projectRequestStatus.length > 0
            ),
          targetUserUuid
        ),
        cancel: convertRow3Request(
          rows
            .map(row => ({
              ...row,
              projectRequestStatus: row.projectRequestStatus?.filter(
                status =>
                  !row.requestCheck &&
                  status.requestStatus === RequestStatus.REQUESTED
              ),
            }))
            .filter(
              row =>
                row.projectRequestStatus && row.projectRequestStatus.length > 0
            ),
          targetUserUuid
        ),
        proxyUserUuid,
      }
      return updateRequestBatch(batchInput)
    },
    []
  )

  const saveRequest = useCallback(
    async (targetUserUuid: string): Promise<APIResponse> => {
      if (!featureFlagService.isEnabled('task-actual-work-application')) {
        return {
          status: 200,
          json: {},
          hasError: false,
          hasWarning: false,
        }
      }
      const edited = data.filter(row => row.edited)
      const response = await updateWorkingRequest(edited, targetUserUuid)
      return response
    },
    [data, updateWorkingRequest]
  )

  const convertRow3Request = (
    items: ProxyRequestRow[],
    userUuid: string
  ): ProxyRequestRequestInput[] => {
    return items.map(row => {
      return {
        userUuid,
        workDate: row.date ?? '',
        requestCheck: row.requestCheck,
        projectRequests: projectRequests(row),
      }
    })
  }

  const projectRequests = (
    row: ProxyRequestRow
  ): RequestStatusRowProjectBody[] => {
    if (!row.projectRequestStatus) return [] as RequestStatusRowProjectBody[]

    const currentProject = store.getState().project?.current?.uuid
    if (!currentProject) return [] as RequestStatusRowProjectBody[]

    return row.projectRequestStatus
      .filter(status => {
        if (status.projectUuid !== currentProject) {
          return false
        }
        if (
          row.requestCheck &&
          (status.requestStatus === RequestStatus.APPROVED ||
            status.requestStatus === RequestStatus.REQUESTED)
        ) {
          return false
        }
        if (
          !row.requestCheck &&
          (status.requestStatus === RequestStatus.SENT_BACK ||
            status.requestStatus === undefined ||
            status.requestStatus === RequestStatus.APPROVED)
        ) {
          return false
        }
        return true
      })
      .map(status => {
        const body = new RequestStatusRowProjectBody()
        body.projectUuid = status.projectUuid
        body.requestUuid = status.requestUuid
        return body
      })
  }

  const fetchUsers = useCallback(
    async (startDate: string, endDate: string): Promise<UserBasic[]> => {
      const formatStart = new DateVO(startDate).formatForApi()!
      const formatEnd = new DateVO(endDate).formatForApi()!
      const response = await fetchTargetUsers({
        startDate: formatStart,
        endDate: formatEnd,
      })
      const src = response.json
      const users: UserBasic[] = src.children.map(v => {
        return {
          uuid: v.user.uuid,
          name: v.user.name,
        } as UserBasic
      })
      return users
    },
    []
  )

  const getStandardWorkingInfo = useCallback(() => {
    const scheduleHour = data.map(value => {
      return value.schedule?.workHour ?? 0
    })
    const workDays = scheduleHour.reduce((prev, cur) => {
      return prev + (cur > 0 ? 1 : 0)
    }, 0)
    const rawStdHour = scheduleHour.reduce((prev, cur) => {
      return prev + cur
    }, 0)
    let sumTime = 0
    let sumDays = 0
    data.forEach(row => {
      sumTime += calculateWorkTime(row)
      if (!!row.actual && !!row.actual.startTime && !!row.actual.endTime) {
        sumDays++
      }
    })

    const diffHours = sumTime - rawStdHour * 3600
    const diffDays = sumDays - workDays
    const diffHoursPercent =
      rawStdHour > 0
        ? Math.round((diffHours / (rawStdHour * 3600)) * 10000) / 100
        : 0
    const diffDaysPercent =
      workDays > 0 ? Math.round((diffDays / workDays) * 10000) / 100 : 0

    return {
      standardWorkDays: workDays,
      standardWorkHours: rawStdHour,
      totalWorkHours: sumTime,
      totalWorkDays: sumDays,
      diffHours: diffHours,
      diffDays: diffDays,
      diffHoursPercent: diffHoursPercent,
      diffDaysPercent: diffDaysPercent,
    } as StandardWorkingInfo
  }, [data])

  const updateRequestCheck = useCallback(
    (
      targetRows: ProxyRequestRow[],
      newValue: boolean,
      gridApi?: any | null | undefined
    ) => {
      const currentProject = store.getState().project?.current?.uuid
      if (!currentProject) return

      const requestCheckField = `project_${currentProject}_requestCheck`

      const validRows = targetRows.filter(row => {
        const hasCurrentProjectWork = row.projectRequestStatus?.some(
          status => status.projectUuid === currentProject
        )
        if (!hasCurrentProjectWork) return false

        const currentProjectStatus = row.projectRequestStatus?.find(
          status => status.projectUuid === currentProject
        )
        if (currentProjectStatus?.requestStatus === RequestStatus.APPROVED) {
          return false
        }

        return true
      })

      validRows.forEach(row => {
        if (row[requestCheckField] !== newValue) {
          row[requestCheckField] = newValue
          row.edited = true
        }
      })

      if (gridApi && validRows.length > 0) {
        validRows.forEach(row => {
          const rowNode = gridApi.getRowNode(row.uuid)
          if (rowNode) {
            rowNode.setDataValue(requestCheckField, newValue)
          }
        })

        gridApi.refreshCells({
          columns: [requestCheckField],
          force: true,
        })
      }

      if (validRows.length > 0) {
        setData(prevData => [...prevData])
        store.dispatch(requireSave())
      }
    },
    []
  )

  return {
    data,
    summaryRow,
    ratioRow,
    refresh,
    saveRequest,
    updateRequestCheck,
    updateWorkingRequest,
    fetchUsers,
    clearData,
    getStandardWorkingInfo,
  }
}
